import { useCallback, useMemo } from "react";

import usePlatformEnv from "./usePlatformEnv";
import useToast from "./useToast";

import { useSoccerPayment } from "./index";
import { useLeagueStore } from "../contexts";

import { useLeagueApiService } from "../services";
import type {
	CreateCompetitionObject,
	EntityRoles,
	LeagueModel,
	LogoModel,
	MediaModel,
	PaginationParamsModel,
	SetupEntityModel
} from "../types";
import { BackendEntityType } from "../types";

const useLeague = () => {
	const leagueService = useLeagueApiService();

	const { setState, setInitial, ...store } = useLeagueStore();

	const { showToast } = useToast();

	const {
		getPaymentRequests,
		checkStripeConnectIntegrationStatus,
		registerStripeConnectAccount,
		getPaymentHistory,
		getTransactionsHistory
	} = useSoccerPayment();

	const { isShowRoleToasts } = usePlatformEnv();

	const methods = useMemo(() => {
		return {
			createLeague: async (leagueData: Omit<LeagueModel, "id">) => {
				const { league } = await leagueService.createLeague(leagueData);
				setState({ league });
				return league;
			},
			setupLeague: async (data: SetupEntityModel & { id: string }) => {
				setState({ loadingStructure: true });
				const { league } = await leagueService.setupLeague(data);
				setState({ structure: league.struct, loadingStructure: false });
				return league;
			},
			updateNewLeague(
				newLeagueData?: Partial<Omit<LeagueModel, "logo">> & {
					logo?: string | null | MediaModel | LogoModel;
				}
			) {
				setState(ctx => ({
					newLeague: { ...ctx.newLeague, ...newLeagueData }
				}));
			},
			updateNewSetupLeague(newSetupLeagueData?: Partial<SetupEntityModel>) {
				setState(ctx => ({
					newSetupLeague: { ...ctx.newSetupLeague, ...newSetupLeagueData }
				}));
			},
			createLeagueCompetition: async (data: Partial<CreateCompetitionObject>, displayToast = false) => {
				try {
					const { competition } = await leagueService.createLeagueCompetition(data);
					setState({ leagueCompetition: competition });

					if (displayToast) {
						showToast({
							text: "New competition was successfully created.",
							noIcon: true
						});
					}
					return competition;
				} catch (error) {
					console.log(error);
					showToast({
						text: "Something went wrong. Please try again later."
					});
				}
			},
			setupLeagueCompetition: async (data: Partial<CreateCompetitionObject>) => {
				try {
					const res = await leagueService.setupLeagueCompetition(data);
					return res.competition.struct;
				} catch (err) {
					showToast({
						text: "Something went wrong. Please try again later."
					});
					console.error("Error: ", err);
					return null;
				}
			},
			getFollowedLeagues: async (params: { search?: string } & PaginationParamsModel) => {
				setState({ loadingFollowedLeagues: true });
				const { leagues, nextToken } = await leagueService.getFollowedLeagues(params);
				setState({ followedLeaguesNextToken: nextToken, loadingFollowedLeagues: false });
				return leagues;
			},
			getLeagues: async (ignoreStateUpdate?: boolean, ignoreLoading?: boolean) => {
				!ignoreLoading && setState({ loading: true });
				const { leagues } = await leagueService.getLeagues();
				setState(ctx => ({
					leagues: ignoreStateUpdate ? ctx.leagues : leagues,
					loading: ignoreLoading ? ctx.loading : false
				}));
				return leagues;
			},
			findLeagues: async ({
				search,
				limit = 10,
				ignoreStateUpdate,
				ignoreLoading
			}: {
				search?: string;
				limit?: number;
				ignoreStateUpdate?: boolean;
				ignoreLoading?: boolean;
			}) => {
				!ignoreLoading && setState({ loading: true });
				const { leagues } = await leagueService.findLeagues({ search, limit });
				setState(ctx => ({
					leagues: ignoreStateUpdate ? ctx.leagues : leagues,
					loading: ignoreLoading ? ctx.loading : false
				}));
				return leagues;
			},
			setSeason: (season?: Partial<CreateCompetitionObject>) => {
				setState({ season });
			},
			getLeagueOverview: async (id: string, ignoreLoading?: boolean) => {
				try {
					!ignoreLoading && setState({ loadingOverview: true });
					const { league } = await leagueService.getLeagueOverview(id);
					setState(ctx => ({
						overview: league,
						loadingOverview: ignoreLoading ? ctx.loadingOverview : false
					}));
					return league;
				} catch {
					showToast({
						text: "League couldn't be found",
						noIcon: true
					});
					return null;
				} finally {
					if (!ignoreLoading) {
						setState({ loadingOverview: false });
					}
				}
			},
			getLeagueInfo: async (id: string, ignoreLoading?: boolean) => {
				try {
					!ignoreLoading && setState({ loadingInfo: true });
					const { league } = await leagueService.getLeagueInfo(id);
					setState(ctx => ({ info: league, loading: ignoreLoading ? ctx.loadingInfo : false }));

					if (league?.auth?.roles && isShowRoleToasts) {
						showToast({
							text: `Roles for league: ${league.auth.roles.join(", ")}`,
							noIcon: true,
							position: "bottom-left"
						});
					}

					return league;
				} catch {
					showToast({
						text: "League couldn't be found",
						noIcon: true
					});
					return null;
				} finally {
					if (!ignoreLoading) {
						setState({ loadingInfo: false });
					}
				}
			},
			getStructure: async (id: string) => {
				setState({ loadingStructure: true });
				const { league } = await leagueService.getStructure(id);
				setState({ structure: league.struct, loadingStructure: false });
				return league.struct;
			},
			getCompetitions: async ({
				leagueId,
				limit = 10,
				before,
				after
			}: {
				leagueId: string;
			} & PaginationParamsModel) => {
				setState({ loadingCompetitions: true });
				if (!after) {
					setState({ competitions: [], nextListingCompetitionsToken: null });
				}

				try {
					const { league, nextToken } = await leagueService.getCompetitions({ leagueId, limit, before, after });
					setState(ctx => ({
						nextListingCompetitionsToken: nextToken,
						competitions: !!before || !!after ? ctx.competitions : league?.competitions || []
					}));

					return {
						nextToken,
						competitions: league?.competitions || []
					};
				} catch (err) {
					console.error(err);
					showToast({ text: "Something went wrong", noIcon: true });
					setState({ nextListingCompetitionsToken: null });

					return {
						nextToken: null,
						competitions: []
					};
				} finally {
					setState({ loadingCompetitions: false });
				}
			},
			setLeagueSettingsModal: (leagueSettingsModal?: { open: boolean; league: LeagueModel }) => {
				setState({ leagueSettingsModal });
			},
			updateLeague: async (
				data: Partial<Omit<LeagueModel, "logo">> & {
					logo?: string | null | MediaModel | LogoModel;
					leagueId: string;
				},
				dontShowToast?: boolean
			) => {
				setState({ loading: true });

				try {
					const { league } = await leagueService.updateLeague(data);

					setState(ctx => ({ overview: { ...ctx.overview, ...league } }));

					!dontShowToast && showToast({ text: "League updated successfully", noIcon: true });
				} catch (error) {
					showToast({ text: "Error updating league", noIcon: true });
					console.log("update failed", error);
				} finally {
					setState({ loading: false });
				}
			},
			getTeamStats: async (leagueId: string) => {
				setState({ loadingTeamStats: true });
				const { items } = await leagueService.getTeamStats(leagueId);

				setState({ loadingTeamStats: false });

				return items || [];
			},
			getPlayerStats: async (leagueId: string) => {
				setState({ loadingPlayerStats: true });
				const { items } = await leagueService.getPlayerStats(leagueId);

				setState({ loadingPlayerStats: false });

				return items || [];
			},
			setUserLeagueRoles: (userLeagueRoles: EntityRoles[]) => {
				setState({ userLeagueRoles });
			},
			getPaymentRequests: async ({
				leagueId,
				before,
				after,
				...rest
			}: {
				leagueId: string;
			} & PaginationParamsModel) => {
				setState({ loadingPaymentRequests: true });

				if (!after) {
					setState({ paymentRequests: [], nextListingPaymentRequestsToken: null });
				}

				try {
					const { nextToken, paymentRequests } = await getPaymentRequests({
						objectId: leagueId,
						objectType: BackendEntityType.SoccerLeague,
						before,
						after,
						...rest
					});

					setState(ctx => ({
						nextListingPaymentRequestsToken: nextToken,
						paymentRequests: !!before || !!after ? ctx.paymentRequests : paymentRequests
					}));

					return {
						nextToken,
						paymentRequests
					};
				} catch (error) {
					console.error(error);
					showToast({ text: "Failed to load leagye payments requests, please try again later." });

					return {
						nextToken: null,
						paymentRequests: []
					};
				} finally {
					setState({ loadingPaymentRequests: false });
				}
			},
			checkPaymentAccount: async (id: string) => {
				setState({ loadingPaymentAccount: true });

				try {
					const stripeAccount = await checkStripeConnectIntegrationStatus({ objectId: id });
					setState({ paymentAccount: stripeAccount });
					return stripeAccount;
				} catch {
					showToast({
						text: "Can't check payment account information",
						noIcon: true
					});
					return null;
				} finally {
					setState({ loadingPaymentAccount: false });
				}
			},
			getRegistrationPaymentAccountLink: async (params: { id: string; redirectLink: string; countryCode: string }) => {
				setState({ registeringPaymentAccount: true });

				try {
					return await registerStripeConnectAccount({
						objectId: params.id,
						objectType: BackendEntityType.SoccerLeague,
						countryCode: params.countryCode,
						refreshLink: params.redirectLink,
						successLink: params.redirectLink
					});
				} catch {
					showToast({
						text: "Can't get a link to register your payment account",
						noIcon: true
					});
					return null;
				} finally {
					setState({ registeringPaymentAccount: false });
				}
			},
			getPaymentHistory: async ({ id, after, limit }: { id: string } & PaginationParamsModel) => {
				setState({ loadingPaymentTransactions: true });

				try {
					if (!after) {
						setState({ paymentTransactions: [], paymentTransactionsNextToken: null });
					}

					const { transactions, nextToken } = await getPaymentHistory({
						objectId: id,
						objectType: BackendEntityType.SoccerLeague,
						after,
						limit
					});

					setState(ctx => ({
						paymentTransactions: !!after ? [...ctx.paymentTransactions, ...(transactions || [])] : transactions,
						paymentTransactionsNextToken: nextToken
					}));
				} catch (err) {
					console.error(err);
					showToast({ text: "Something went wrong", noIcon: true });
				} finally {
					setState({ loadingPaymentTransactions: false });
				}
			},
			getTransactionsHistory: async ({
				id,
				before,
				after,
				limit
			}: {
				id: string;
			} & PaginationParamsModel) => {
				setState({ loadingPaymentTransactions: true });

				try {
					if (!after) {
						setState({ paymentTransactions: [], paymentTransactionsNextToken: null });
					}

					const { transactions, nextToken } = await getTransactionsHistory({
						objectId: id,
						objectType: BackendEntityType.SoccerLeague,
						before,
						after,
						limit
					});

					setState(ctx => ({
						paymentTransactionsNextToken: nextToken,
						paymentTransactions: !!before || !!after ? ctx.paymentTransactions : transactions
					}));

					return {
						nextToken,
						transactions
					};
				} catch (err) {
					console.error(err);
					showToast({ text: "Something went wrong", noIcon: true });

					return {
						nextToken: null,
						transactions: []
					};
				} finally {
					setState({ loadingPaymentTransactions: false });
				}
			},
			resetStore() {
				setInitial();
			}
		};
	}, [
		leagueService,
		setState,
		showToast,
		setInitial,
		isShowRoleToasts,
		getPaymentRequests,
		checkStripeConnectIntegrationStatus,
		registerStripeConnectAccount,
		getPaymentHistory,
		getTransactionsHistory
	]);

	const isPaymentAccountValid = useMemo(
		() =>
			!!store?.paymentAccount?.detailsSubmitted &&
			!!store?.paymentAccount?.chargesEnabled &&
			!!store?.paymentAccount?.transfersEnabled,
		[store]
	);

	const getData = useCallback(() => {
		return { ...store, isPaymentAccountValid };
	}, [store, isPaymentAccountValid]);

	return { ...methods, getData };
};

export default useLeague;
